import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import { suppressKeyboardEventForInput } from 'components/AgGrid/utils';
import type {
    ContractStatusEnum,
    Maybe,
    WorkOrderActKc2Data,
    WorkOrderPriceItem,
    WorkOrderSeasonData,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import {
    ActKc2StatusEnum,
    ChapterEnum,
    CostGroupEnum,
    SkzsActKc2StatusEnum,
    WorkOrderPriceTypeEnum,
} from 'gql/graphql';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { getActCellClass, useScrolledToActId } from 'pages/Project/Common/hooks/useScrolledToActId';
import { DeviatedColumnTitle } from 'utils/tableCells';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { TitleActKC2Columns } from '../../WorkOrderPrice/TitleActKC2Columns';
import {
    ActCostColType,
    ActCostRenderer,
    ActKC2CommentCellRenderer,
    ActSkzsCellRenderer,
    ActSkzsHeaderRenderer,
    ActVolumeColType,
    ActVolumeRenderer,
} from '../cellRenderers';
import { getActValueByKey } from '../helpers';

interface IUseActKC2ColumnsProps {
    workOrderActs: WorkOrderActKc2Data[] | undefined | null;
    seasons: WorkOrderSeasonData[];
    contractStatus: ContractStatusEnum | undefined;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    isDisabledByProjectStatus: boolean;
    onActEditRefresh: () => void;
    costRounding: number;
    skzsPanelRefresh: () => void;
    refreshActs: () => void;
    useAgCellRowDataRefresh: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams: any;
}

const calcSkzsColWidth = (actStatus: ActKc2StatusEnum, actSkzsStatus: Maybe<SkzsActKc2StatusEnum | undefined>) => {
    let baseWidth = 120;

    if (actSkzsStatus === SkzsActKc2StatusEnum.Updated && actStatus !== ActKc2StatusEnum.Accepted) {
        baseWidth += 20;
    }

    if (actStatus === ActKc2StatusEnum.InProgress) {
        baseWidth += 35;
    }

    return baseWidth;
};

export const useWorkOrderActsColumns = (props: IUseActKC2ColumnsProps) => {
    const {
        workOrderActs,
        contractStatus,
        workOrderStatus,
        isDisabledByProjectStatus,
        onActEditRefresh,
        costRounding,
        skzsPanelRefresh,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        refreshActs,
        seasons: workOrderSeasons,
    } = props;

    const { scrolledToActId, withHighLightManager, scrolledToActIdsArr } = useScrolledToActId();

    return useMemo<(ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[]>(() => {
        let isOddSeason = true;
        const currentSeasonId = workOrderActs?.[0]?.seasonId;

        return (workOrderActs ?? []).map(
            ({
                id,
                seasonId,
                status,
                number,
                date,
                acceptedVolumeDeviation,
                acceptedCostDeviation,
                planVolumeDeviation,
                planCostDeviation,
                isDistributionOfMtrInflation,
                fullNumber,
                isInactive,
                isCorrection,
                isSKzS,
                statusSKzS,
                canCreateCorrection,
                correctionDate,
                isSKzSUpdated,
                isSKzSCopyLock,
                costGroup,
                objectId,
            }) => {
                if (seasonId !== currentSeasonId) {
                    isOddSeason = !isOddSeason;
                }

                const isOther = costGroup === CostGroupEnum.Others;

                const highlightedActId = scrolledToActIdsArr
                    ? scrolledToActIdsArr.find((i) => i === id)
                    : scrolledToActId;

                const cellClass =
                    isSKzS && statusSKzS === SkzsActKc2StatusEnum.New
                        ? 'skzs-blue'
                        : getActCellClass(id, isOddSeason, highlightedActId);

                const inflationSeason: (ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[] = [
                    {
                        headerName: 'Сезон инфляции',
                        width: 150,
                        cellRenderer: ({ data }: { data: WorkOrderPriceItem }) => {
                            if (!data) {
                                return '';
                            }

                            if (
                                data.hasEmptyActPlanVolume ||
                                (data?.type !== WorkOrderPriceTypeEnum.MtrContractorMaterial &&
                                    data?.type !== WorkOrderPriceTypeEnum.MtrContractorEquipment)
                            ) {
                                return '';
                            }

                            if (data) {
                                const currentSeason = data?.seasonValues?.find?.(
                                    (season) => season.seasonId === seasonId,
                                );

                                const currentDataAct = currentSeason?.actValues?.find((act) => act.actId === id);

                                if (currentDataAct?.isActKC2DistributionOfMtrInflation === true) {
                                    if (currentDataAct?.actInflationSeasonId === null) {
                                        return 'Не учитывать';
                                    }

                                    const currentInflationSeason = workOrderSeasons?.find?.(
                                        ({ id: inflationSeasonId }) =>
                                            inflationSeasonId === currentDataAct?.actInflationSeasonId,
                                    );

                                    return currentInflationSeason?.year ?? '';
                                }
                            }

                            return '';
                        },
                        headerClass: `ag-right-aligned-header ${cellClass}`,
                        cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                    },
                ];

                const volumeColPlanSkzs: ColDef<WorkOrderPriceItem>[] = [
                    {
                        headerName: 'Объем (План СКзС)',
                        width: calcSkzsColWidth(status, statusSKzS),
                        headerComponent: ActSkzsHeaderRenderer,
                        headerComponentParams: {
                            actId: id,
                            actStatus: status,
                            isSKzSUpdated,
                            seasonId,
                            contractStatus,
                            isDisabledByProjectStatus,
                            onSuccess: onActEditRefresh,
                            isSKzSCopyLock,
                        },
                        valueGetter: getActValueByKey({
                            valueKey: 'actVolumePlanSKzS',
                            seasonId,
                            costRounding,
                            actId: id,
                        }),
                        cellRenderer: ActSkzsCellRenderer,
                        cellRendererParams: {
                            actId: id,
                            seasonId,
                            contractStatus,
                            isDisabledByProjectStatus,
                            skzsPanelRefresh,
                            useAgCellRowDataRefresh,
                            useAgCellRowDataRefreshParams,
                        },
                        headerClass: `${cellClass}`,
                        cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                    },
                ];

                const volumeColPlan: (ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[] = [
                    {
                        field: `actKC2PlanVolume_${id}` as any,
                        // eslint-disable-next-line react/display-name
                        headerComponent: () => (
                            <div style={{ display: 'flex', width: '100%' }}>
                                <DeviatedColumnTitle
                                    title="Объем (План)"
                                    deviation={status !== ActKc2StatusEnum.Accepted ? planVolumeDeviation : null}
                                    tooltipText="Превышает остаток по сезону"
                                />
                            </div>
                        ),
                        width: 155,
                        minWidth: 155,
                        cellRenderer: ActVolumeRenderer,
                        cellRendererParams: {
                            seasonId,
                            workOrderStatus,
                            actId: id,
                            deviationKey: 'actVolumePlanDeviation',
                            valueKey: 'actVolumePlan',
                            contractStatus,
                            isDisabledByProjectStatus,
                            colType: ActVolumeColType.planVolume,
                            skzsPanelRefresh,
                            useAgCellRowDataRefresh,
                            useAgCellRowDataRefreshParams,
                            refreshActs,
                        },
                        headerClass: `ag-right-aligned-header ${cellClass}`,
                        cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                        suppressKeyboardEvent: suppressKeyboardEventForInput,
                    },
                ];

                const costColPlan: (ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[] = [
                    {
                        field: `actKC2PlanCost_${id}` as any,
                        // eslint-disable-next-line react/display-name
                        headerComponent: () => (
                            <DeviatedColumnTitle
                                title="Стоимость (План)"
                                deviation={status !== ActKc2StatusEnum.Accepted ? planCostDeviation : null}
                                tooltipText="Превышает остаток по сезону"
                            />
                        ),
                        headerName: 'Стоимость (План)',
                        width: 150,
                        cellRenderer: ActCostRenderer,
                        cellRendererParams: {
                            seasonId,
                            workOrderStatus,
                            actId: id,
                            valueKey: 'actCostPlan',
                            deviationKey: 'actCostPlanDeviation',
                            contractStatus,
                            isDisabledByProjectStatus,
                            decimal: costRounding,
                            colType: ActCostColType.planCost,
                            skzsPanelRefresh,
                            useAgCellRowDataRefresh,
                            useAgCellRowDataRefreshParams,
                            refreshActs,
                        },
                        headerClass: cellClass,
                        cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                        suppressKeyboardEvent: suppressKeyboardEventForInput,
                    },
                ];

                return {
                    seasonId,
                    id,
                    colId: id,
                    // eslint-disable-next-line react/display-name
                    headerGroupComponent: () => (
                        <TitleActKC2Columns
                            id={id}
                            number={number}
                            status={status}
                            isSKzS={isSKzS}
                            date={date}
                            costGroup={costGroup}
                            onRefresh={onActEditRefresh}
                            contractStatus={contractStatus}
                            workOrderStatus={workOrderStatus}
                            isDisabledByProjectStatus={isDisabledByProjectStatus}
                            chapter={ChapterEnum.Chapter1_7}
                            workOrderSubObjectId={objectId}
                            seasonId={seasonId}
                            fullNumber={fullNumber}
                            isInactive={isInactive}
                            isCorrection={isCorrection}
                            canCreateCorrection={canCreateCorrection}
                            correctionDate={correctionDate}
                            withHighLightManager={withHighLightManager}
                            skzsPanelRefresh={skzsPanelRefresh}
                        />
                    ),
                    headerName: formatActTitle(
                        isCorrection ? fullNumber : number,
                        isCorrection ? correctionDate : date,
                    ),
                    marryChildren: true,
                    headerClass: cellClass,
                    children: [
                        ...(isDistributionOfMtrInflation ? inflationSeason : []),
                        ...(isSKzS ? volumeColPlanSkzs : []),
                        ...(!isOther && status !== ActKc2StatusEnum.Accepted ? volumeColPlan : []),
                        ...(status !== ActKc2StatusEnum.Accepted ? costColPlan : []),
                        ...(!isOther
                            ? [
                                  {
                                      colId: `${id}`,
                                      // eslint-disable-next-line react/display-name
                                      headerComponent: () => (
                                          <DeviatedColumnTitle
                                              title="Объем (Согласовано)"
                                              deviation={acceptedVolumeDeviation}
                                              tooltipText="Превышает остаток по сезону"
                                          />
                                      ),
                                      width: 155,
                                      cellRenderer: ActVolumeRenderer,
                                      cellRendererParams: {
                                          seasonId,
                                          workOrderStatus,
                                          actId: id,
                                          deviationKey: 'actVolumeAcceptedDeviation',
                                          valueKey: 'actVolumeAccepted',
                                          contractStatus,
                                          isDisabledByProjectStatus,
                                          colType: ActVolumeColType.acceptedVolume,
                                          skzsPanelRefresh,
                                          useAgCellRowDataRefresh,
                                          useAgCellRowDataRefreshParams,
                                          refreshActs,
                                      },
                                      headerClass: `ag-right-aligned-header ${cellClass}`,
                                      cellClass: `ag-right-aligned-cell ${cellClass} ${
                                          isInactive ? 'inactive-act' : undefined
                                      }`,
                                      suppressKeyboardEvent: suppressKeyboardEventForInput,
                                  },
                              ]
                            : []),
                        {
                            colId: `${id}`,
                            field: `actKC2_${id}_cost_accepted`,
                            // eslint-disable-next-line react/display-name
                            headerComponent: () => (
                                <DeviatedColumnTitle
                                    title="Стоимость (Согласовано)"
                                    deviation={acceptedCostDeviation}
                                    tooltipText="Превышает остаток по сезону"
                                />
                            ),
                            headerName: 'Стоимость (Согласовано)',
                            width: 155,
                            cellRenderer: ActCostRenderer,
                            cellRendererParams: {
                                seasonId,
                                workOrderStatus,
                                actId: id,
                                valueKey: 'actCostAccepted',
                                deviationKey: 'actCostAcceptedDeviation',
                                contractStatus,
                                isDisabledByProjectStatus,
                                decimal: costRounding,
                                colType: ActCostColType.acceptedCost,
                                skzsPanelRefresh,
                                useAgCellRowDataRefresh,
                                useAgCellRowDataRefreshParams,
                                refreshActs,
                            },
                            headerClass: cellClass,
                            cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                            suppressKeyboardEvent: suppressKeyboardEventForInput,
                        },
                        {
                            headerName: 'Комментарий',
                            field: 'comment',
                            colId: `comment_${id}`,
                            cellRenderer: ActKC2CommentCellRenderer,
                            valueGetter: (params) =>
                                params.data?.seasonValues
                                    ?.find((s) => s.seasonId === seasonId)
                                    ?.actValues?.find((a) => a.actId === id)?.comment,
                            cellRendererParams: {
                                actId: id,
                                actNumber: number,
                                actDate: date,
                                seasonId,
                                workOrderStatus,
                                contractStatus,
                                isDisabledByProjectStatus,
                                skzsPanelRefresh,
                                useAgCellRowDataRefresh,
                                useAgCellRowDataRefreshParams,
                            },
                            headerClass: cellClass,
                            cellClass: `${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                        },
                    ],
                };
            },
        );
    }, [
        contractStatus,
        costRounding,
        isDisabledByProjectStatus,
        onActEditRefresh,
        refreshActs,
        scrolledToActId,
        scrolledToActIdsArr,
        skzsPanelRefresh,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        withHighLightManager,
        workOrderActs,
        workOrderSeasons,
        workOrderStatus,
    ]);
};
